// Content filter — mirror of the backend
// (``services/content_filter.py``) for instant UX feedback on forms.
// The backend remains authoritative; everything checked here is also
// re-checked server-side before persistence. Keeping the logic in
// sync is a hand-maintained contract — when the backend list grows,
// so should this one.
//
// API:
//
//   * ``checkText(text, { kind, minLength, maxLength }) -> Verdict``
//   * ``isOk(verdict)`` — sugar
//   * ``filterErrorMessage(verdict, t)`` — pull a localised string
//
// Verdict shape:
//
//   { ok: boolean, error: string, hit: string, cleaned: string }
//
// Error slugs match the backend so a frontend pre-flight rejection
// and a server rejection produce the same toast.

// ---------------------------------------------------------------------------
// Wordlists — keep in sync with services/content_filter_words.py
// ---------------------------------------------------------------------------

const SLURS = [
  // English
  'nigger', 'nigga', 'faggot', 'fag', 'tranny', 'kike', 'chink',
  'spic', 'wetback', 'raghead', 'gook', 'dyke', 'retard', 'retarded',
  // French
  'negre', 'bougnoule', 'pedale', 'tarlouze',
];

const PROFANITY = [
  // English
  'fuck', 'fucker', 'fucking', 'shit', 'bitch', 'bastard', 'asshole',
  'cunt', 'dick', 'cock', 'pussy', 'whore', 'slut', 'wank', 'wanker',
  'twat',
  // French
  'merde', 'putain', 'salope', 'connard', 'connasse', 'enculer',
  'enculé', 'encule', 'bite', 'couilles', 'chier',
];

const ALLOWLIST = new Set([
  'scunthorpe', 'penistone', 'lightwater', 'assassin', 'assist',
  'assume', 'class', 'classic', 'classification', 'compass',
  'embarrass', 'harass', 'passage', 'password', 'grass',
  'associate', 'passion', 'ass',
]);

// ---------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------

const LEET_MAP = {
  '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't',
  '@': 'a', '$': 's', '!': 'i', '|': 'i',
  µ: 'u', ø: 'o', ç: 'c',
};

const NON_LETTERS_WITHIN_TOKEN = /[^a-z]+/g;
const REPEAT_CHARS = /(.)\1{2,}/g;

function stripAccents(s) {
  return s.normalize('NFKD').replace(/[̀-ͯ]/g, '');
}

function applyLeet(s) {
  let out = '';
  for (const ch of s) {
    out += LEET_MAP[ch] ?? ch;
  }
  return out;
}

function normaliseTokens(text) {
  if (!text) return [];
  let out = stripAccents(text).toLowerCase();
  out = applyLeet(out);
  out = out.replace(REPEAT_CHARS, '$1$1');
  return out
    .split(/\s+/)
    .filter(Boolean)
    .map((tok) => tok.replace(NON_LETTERS_WITHIN_TOKEN, ''))
    .filter(Boolean);
}

// ---------------------------------------------------------------------------
// Matchers
// ---------------------------------------------------------------------------

function sortedWords(words) {
  // Distinct + longest first so ``fucker`` matches before ``fuck``.
  return [...new Set(words)].sort((a, b) => b.length - a.length);
}

const SLUR_LIST = sortedWords(SLURS);
const PROFANITY_LIST = sortedWords(PROFANITY);

// Token match: banned word is a whole token after normalisation.
// Catches ``f*ck`` / ``f.u.c.k`` (each token reduces to ``fuck``)
// but not ``classic``.
function tokenHit(words, tokens) {
  if (!tokens.length) return null;
  const tokenSet = new Set(tokens);
  for (const w of words) {
    if (tokenSet.has(w) && !ALLOWLIST.has(w)) return w;
  }
  return null;
}

// Substring match on the compacted text (all tokens joined). Used
// for identity surfaces (username/title) where smushed-CamelCase
// is the common dodge. Skips when any input token is in the
// allowlist and contains the banned word (Scunthorpe → cunt).
function substringHit(words, compact, tokens) {
  if (!compact) return null;
  for (const w of words) {
    if (!compact.includes(w)) continue;
    if (tokens.some((t) => ALLOWLIST.has(t) && t.includes(w))) continue;
    return w;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Per-kind rules
// ---------------------------------------------------------------------------

const USERNAME_CHAR_RE = /^[A-Za-z0-9_\-.]+$/;
const USERNAME_EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+/;
const USERNAME_PHONE_RE = /\+?\d[\d \-().]{6,}/;
const URL_LIKE_RE = /\b(?:https?:\/\/|www\.|[a-z0-9-]+\.(?:com|net|org|io|co|fr|app|xyz|info|biz))/i;

const RESERVED_USERNAMES = new Set([
  'admin', 'administrator', 'root', 'system', 'moderator', 'mod',
  'support', 'help', 'writeon', 'write-on', 'plumescript', 'official', 'staff',
  'anonymous', 'guest', 'null', 'undefined', 'me', 'you',
  'api', 'www', 'mail', 'email',
]);

const KIND_DEFAULTS = {
  username: { min: 3, max: 30 },
  title: { min: 1, max: 200 },
  display: { min: 0, max: 200 },
  body: { min: 0, max: 50_000 },
  general: { min: 0, max: 5_000 },
};

/**
 * @param {string|null|undefined} text
 * @param {{ kind?: 'username'|'title'|'display'|'body'|'general', minLength?: number, maxLength?: number }} [opts]
 * @returns {{ ok: boolean, error: string, hit: string, cleaned: string }}
 */
export function checkText(text, opts = {}) {
  const kind = KIND_DEFAULTS[opts.kind] ? opts.kind : 'general';
  const cleaned = (text ?? '').trim();
  const tokens = normaliseTokens(cleaned);
  const compact = tokens.join('');
  const { min, max } = KIND_DEFAULTS[kind];
  const lo = opts.minLength ?? min;
  const hi = opts.maxLength ?? max;

  if (cleaned.length < lo) return { ok: false, error: 'too_short', hit: '', cleaned };
  if (cleaned.length > hi) return { ok: false, error: 'too_long', hit: '', cleaned };

  if (kind === 'username') {
    if (!USERNAME_CHAR_RE.test(cleaned)) return { ok: false, error: 'invalid_chars', hit: '', cleaned };
    if (RESERVED_USERNAMES.has(cleaned.toLowerCase())) return { ok: false, error: 'reserved', hit: '', cleaned };
    if (USERNAME_EMAIL_RE.test(cleaned) || USERNAME_PHONE_RE.test(cleaned)) {
      return { ok: false, error: 'pii', hit: '', cleaned };
    }
    if (URL_LIKE_RE.test(cleaned)) return { ok: false, error: 'url_like', hit: '', cleaned };
  }

  const isIdentity = kind === 'username' || kind === 'title';
  let slur = tokenHit(SLUR_LIST, tokens);
  if (!slur && isIdentity) slur = substringHit(SLUR_LIST, compact, tokens);
  if (slur) return { ok: false, error: 'slur', hit: slur, cleaned };

  let prof = tokenHit(PROFANITY_LIST, tokens);
  if (!prof && isIdentity) prof = substringHit(PROFANITY_LIST, compact, tokens);
  if (prof && isIdentity) {
    return { ok: false, error: 'profanity', hit: prof, cleaned };
  }

  return { ok: true, error: '', hit: '', cleaned };
}

export function isOk(verdict) {
  return Boolean(verdict?.ok);
}

/**
 * Look up a localised error message for a verdict, falling back to a
 * generic one. ``t`` is the i18n translator from ``useT()``.
 */
export function filterErrorMessage(verdict, t) {
  if (!verdict || verdict.ok) return '';
  const key = `content_filter.error.${verdict.error}`;
  const msg = t(key);
  // The i18n helper returns the key when the translation is missing;
  // if that happens fall back to the generic string.
  if (msg === key) return t('content_filter.error.generic');
  return msg;
}
